/**
 * Shared amenity catalog for short-let/hotel listings, used by the "list a
 * property" form (checkbox input) and the property details page (grouped
 * display), so both sides agree on the same keys/categories/labels.
 *
 * `amenities` stays a flat array of these keys in the DB (same TEXT/JSON
 * column as before), grouping only happens at render/input time.
 */
export const AMENITY_CATEGORIES = [
  {
    id: 'kitchen',
    label: 'Kitchen',
    amenities: ['fridge', 'microwave', 'gas_cooker', 'kettle', 'blender', 'dishwasher', 'dining_table'],
  },
  {
    id: 'bathroom',
    label: 'Bathroom',
    amenities: ['hot_water', 'bathtub', 'hair_dryer', 'toiletries', 'towels'],
  },
  {
    id: 'bedroom_laundry',
    label: 'Bedroom & Laundry',
    amenities: ['washing_machine', 'iron', 'wardrobe', 'extra_bedding'],
  },
  {
    id: 'entertainment_tech',
    label: 'Entertainment & Technology',
    amenities: ['wifi', 'smart_tv', 'netflix', 'sound_system'],
  },
  {
    id: 'outdoors',
    label: 'Outdoors',
    amenities: ['pool', 'balcony', 'garden', 'bbq_grill'],
  },
  {
    id: 'safety_security',
    label: 'Safety & Security',
    amenities: ['cctv', 'gated_estate', 'fire_extinguisher', 'security_guard', 'smoke_alarm'],
  },
  {
    id: 'general',
    label: 'General',
    amenities: ['air_conditioning', 'generator', 'inverter', 'parking', 'elevator', 'gym', 'workspace'],
  },
];

export const AMENITY_LABELS = {
  fridge: 'Refrigerator',
  microwave: 'Microwave',
  gas_cooker: 'Gas cooker',
  kettle: 'Electric kettle',
  blender: 'Blender',
  dishwasher: 'Dishwasher',
  dining_table: 'Dining table',
  hot_water: 'Hot water',
  bathtub: 'Bathtub',
  hair_dryer: 'Hair dryer',
  toiletries: 'Free toiletries',
  towels: 'Towels',
  washing_machine: 'Washing machine',
  iron: 'Iron',
  wardrobe: 'Wardrobe',
  extra_bedding: 'Extra bedding',
  wifi: 'Free WiFi',
  smart_tv: 'Smart TV',
  netflix: 'Netflix / streaming',
  sound_system: 'Sound system',
  pool: 'Swimming pool',
  balcony: 'Balcony',
  garden: 'Garden',
  bbq_grill: 'BBQ grill',
  cctv: 'CCTV',
  gated_estate: 'Gated estate',
  fire_extinguisher: 'Fire extinguisher',
  security_guard: '24-hour security',
  smoke_alarm: 'Smoke alarm',
  air_conditioning: 'Air conditioning',
  generator: 'Generator',
  inverter: 'Inverter / backup power',
  parking: 'Free parking',
  elevator: 'Elevator',
  gym: 'Gym access',
  workspace: 'Dedicated workspace',
};

export function amenityLabel(key) {
  return AMENITY_LABELS[key] || key;
}

/**
 * Groups a flat array of amenity keys into { category, amenities: [{key,
 * label}] } buckets, in AMENITY_CATEGORIES order. Anything not in the
 * catalog (legacy free-text values from before this catalog existed)
 * falls into a trailing "Other" bucket instead of being dropped.
 */
export function groupAmenities(keys) {
  const remaining = new Set(keys);
  const groups = AMENITY_CATEGORIES.map((category) => {
    const matched = category.amenities.filter((key) => remaining.has(key));
    matched.forEach((key) => remaining.delete(key));
    return { id: category.id, label: category.label, amenities: matched.map((key) => ({ key, label: amenityLabel(key) })) };
  }).filter((group) => group.amenities.length > 0);

  if (remaining.size > 0) {
    groups.push({
      id: 'other',
      label: 'Other',
      amenities: Array.from(remaining).map((key) => ({ key, label: amenityLabel(key) })),
    });
  }
  return groups;
}
